import { useTranslator } from "@eo-locale/react";

import { array } from "yup";

import {
  Field,
  HTMLWrapper,
  CheckboxGroup,
  Checkbox,
  Wrap,
  WrapItem,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface CheckboxQuestionProps {
  question: Question;
}

export const validate = async (
  answer: string[],
  question: Question
): Promise<string[]> => {
  let schema = array().nullable();
  if (question.required) {
    schema = schema.required();
  }
  if (question.checkboxAnswersLimit) {
    schema = schema.max(question.checkboxAnswersLimit);
  }
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const CheckBoxQuestion = ({ question }: CheckboxQuestionProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || [];

  const handleChange = (value: string[]): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: value.length ? value.map(Number) : null
      })
    );
  };

  return (
    <Field.Root
      my={4}
      as="fieldset"
      id={`${question.id}`}
      invalid={error.length > 0}
      required={question.required}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label as="legend">
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <CheckboxGroup
        name="checkboxQuestion-checkbox"
        onValueChange={handleChange}
        value={answer.map(String)}
      >
        <Wrap
          gap={4}
          direction={{
            base: "column",
            lg: question.horizontal ? "row" : "column"
          }}
        >
          {[...question.questionOptions]
            .filter((option) => !option.deleted)
            .sort((a, b) => a.sequence - b.sequence)
            .map((option) => (
              <WrapItem key={option.id}>
                <Checkbox
                  pl={1}
                  value={`${option.id}`}
                  size={{ base: "lg", lg: "md" }}
                  invalid={error.length > 0}
                  disabled={status === "saving"}
                  data-testid={`checkboxQuestion-${option.name}`}
                >
                  {option.name}
                </Checkbox>
              </WrapItem>
            ))}
        </Wrap>
      </CheckboxGroup>
      {error.length > 0 && (
        <Field.ErrorText data-testid="checkboxQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      <Field.HelperText>{question.notes}</Field.HelperText>
    </Field.Root>
  );
};

export default CheckBoxQuestion;
